/**
 * Shared issue-triage queue (PR-4 builder reorg).
 *
 * Two consumers, same table + drawer:
 *   - Panorama IA → "Cola de issues" tab (cross-project; report column
 *     + report filter visible).
 *   - Report editor AI tab → "Issues" pill (pre-filtered to the draft
 *     via `fixedReportKey`; report column + filter hidden).
 *
 * Port of the hp-portal `IssuesPage.tsx` semantics (heuristic/status
 * pills, drawer actions promote / resolve / dismiss / reopen) restyled
 * with the builder's Omnitok conventions.
 */

import {
  Badge,
  Button,
  Card,
  Select,
  type SelectOption,
} from "@omnitok/ui";
import {
  AlertCircle,
  CheckCircle2,
  RotateCcw,
  X,
  XCircle,
} from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import { useDialogFocus } from "@pervasivemind/dashboard-render";
import { useCallback, useEffect, useRef, useState } from "react";

import { useT } from "@/i18n";
import { draftsApi } from "@/lib/builder-api";
import {
  adminIssuesApi,
  formatDateTime,
  formatRelativeTime,
  truncateText,
  type IssueDetail,
  type IssueDismissReason,
  type IssueResolveAction,
  type PaginatedIssues,
} from "@/lib/admin-ai-api";

const PAGE_SIZE = 20;

/** Heuristics the capture pipeline emits today — unknown values render raw. */
const KNOWN_HEURISTICS = [
  "tool_execution_error",
  "empty_result",
  "max_iterations",
  "empty_response",
  "llm_cannot_answer",
  "user_reformulation",
  "user_thumbs_down",
] as const;

const HEURISTIC_COLORS: Record<string, string> = {
  tool_execution_error: "bg-error/10 text-error-dark border-error/40",
  empty_result: "bg-warning/20 text-warning-dark border-warning/40",
  max_iterations: "bg-orange-100 text-orange-700 border-orange-200",
  empty_response: "bg-neutral-100 text-neutral-700 border-neutral-200",
  llm_cannot_answer: "bg-purple-100 text-purple-700 border-purple-200",
  user_reformulation: "bg-info/15 text-info-dark border-info/40",
  user_thumbs_down: "bg-pink-100 text-pink-700 border-pink-200",
};

const KNOWN_STATUSES = ["new", "in_review", "resolved", "dismissed"] as const;

const STATUS_COLORS: Record<string, string> = {
  new: "bg-info/15 text-info-dark border-info/40",
  in_review: "bg-warning/20 text-warning-dark border-warning/40",
  resolved: "bg-success/15 text-success-dark border-success/40",
  dismissed: "bg-neutral-100 text-neutral-600 border-neutral-200",
};

const FALLBACK_PILL = "bg-neutral-100 text-neutral-600 border-neutral-200";

interface IssuesQueueProps {
  /** Report-scoped mode: pre-filter + hide the report column/filter. */
  fixedReportKey?: string;
  /** Fired after any status-changing action (badge refresh hooks). */
  onChanged?: () => void;
  /**
   * Open-issue count, when the caller already has it.
   *
   * The tab and sidebar badges show OPEN issues; this list defaults to
   * every status, so the two numbers legitimately differ (57 next to
   * 769) and the screen read like a contradiction. Passing the open
   * count lets the line say which is which instead of printing a bare
   * total. Absent → the line just reports the total.
   */
  openCount?: number | null;
}

export function IssuesQueue({
  fixedReportKey,
  onChanged,
  openCount,
}: IssuesQueueProps): JSX.Element {
  const { t, locale } = useT();

  // Filters
  const [reportFilter, setReportFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [heuristicFilter, setHeuristicFilter] = useState("");
  const [page, setPage] = useState(1);

  // Data
  const [listing, setListing] = useState<PaginatedIssues | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportKeys, setReportKeys] = useState<string[]>([]);

  // Drawer
  const [selected, setSelected] = useState<IssueDetail | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const heuristicLabel = useCallback(
    (h: string): string =>
      (KNOWN_HEURISTICS as readonly string[]).includes(h)
        ? t(`issues.heuristic.${h}`)
        : h,
    [t],
  );
  const statusLabel = useCallback(
    (s: string): string =>
      (KNOWN_STATUSES as readonly string[]).includes(s)
        ? t(`issues.status.${s}`)
        : s,
    [t],
  );

  const loadList = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminIssuesApi.list({
        report_key: fixedReportKey ?? (reportFilter || undefined),
        status: statusFilter || undefined,
        heuristic: heuristicFilter || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setListing(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [fixedReportKey, reportFilter, statusFilter, heuristicFilter, page, t]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // Report filter options — cross-project mode only.
  useEffect(() => {
    if (fixedReportKey) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await draftsApi.list();
        if (cancelled) return;
        setReportKeys(res.drafts.map((d) => d.key).sort());
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t("common.error"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fixedReportKey, t]);

  const openIssue = async (id: number): Promise<void> => {
    setDrawerLoading(true);
    try {
      setSelected(await adminIssuesApi.detail(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleActionDone = async (fresh: IssueDetail): Promise<void> => {
    setSelected(fresh);
    await loadList();
    onChanged?.();
  };

  const totalPages = listing
    ? Math.max(1, Math.ceil(listing.total / PAGE_SIZE))
    : 1;

  const statusOptions: SelectOption[] = [
    { value: "", label: t("issues.filter.allStatuses") },
    ...KNOWN_STATUSES.map((s) => ({ value: s, label: statusLabel(s) })),
  ];
  const heuristicOptions: SelectOption[] = [
    { value: "", label: t("issues.filter.allHeuristics") },
    ...KNOWN_HEURISTICS.map((h) => ({ value: h, label: heuristicLabel(h) })),
  ];
  const reportOptions: SelectOption[] = [
    { value: "", label: t("issues.filter.allReports") },
    ...reportKeys.map((k) => ({ value: k, label: k })),
  ];

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark">
          <AlertCircle size={14} className="shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            aria-label={t("common.close")}
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {!fixedReportKey && (
          <Select
            options={reportOptions}
            value={reportFilter}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              setReportFilter(e.target.value);
              setPage(1);
            }}
            aria-label={t("issues.filter.report")}
          />
        )}
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          aria-label={t("issues.filter.status")}
        />
        <Select
          options={heuristicOptions}
          value={heuristicFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setHeuristicFilter(e.target.value);
            setPage(1);
          }}
          aria-label={t("issues.filter.heuristic")}
        />
        {listing && (
          <span className="text-sm text-neutral-500">
            {statusFilter !== "" || heuristicFilter !== "" || reportFilter !== ""
              ? t("issues.countFiltered", { count: listing.total })
              : openCount !== undefined && openCount !== null
                ? t("issues.countAll", {
                    count: listing.total,
                    open: openCount,
                  })
                : t("issues.total", { count: listing.total })}
          </span>
        )}
      </div>

      {/* Table */}
      <Card variant="bordered" noPadding>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("issues.table.id")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("issues.table.created")}
                </th>
                {!fixedReportKey && (
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("issues.table.report")}
                  </th>
                )}
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("issues.table.heuristic")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("issues.table.question")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("issues.table.status")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading ? (
                <tr>
                  <td
                    colSpan={fixedReportKey ? 5 : 6}
                    className="px-4 py-8 text-center"
                  >
                    <span className="inline-flex items-center gap-2 text-sm text-neutral-500">
                      <Spinner size={14} ariaLabel={t("common.loading")} />
                      {t("common.loading")}
                    </span>
                  </td>
                </tr>
              ) : (listing?.items.length ?? 0) === 0 ? (
                <tr>
                  <td
                    colSpan={fixedReportKey ? 5 : 6}
                    className="px-4 py-8 text-center text-neutral-500"
                  >
                    {t("issues.empty")}
                  </td>
                </tr>
              ) : (
                listing?.items.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => void openIssue(row.id)}
                    className="cursor-pointer transition-colors hover:bg-neutral-50"
                  >
                    <td className="px-4 py-2.5 font-mono text-xs text-neutral-700">
                      #{row.id}
                    </td>
                    <td
                      className="whitespace-nowrap px-4 py-2.5 text-xs text-neutral-600"
                      title={formatDateTime(row.created_at)}
                    >
                      {formatRelativeTime(row.created_at, locale)}
                    </td>
                    {!fixedReportKey && (
                      <td className="px-4 py-2.5 text-neutral-700">
                        {row.report_key}
                      </td>
                    )}
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${
                          HEURISTIC_COLORS[row.heuristic] ?? FALLBACK_PILL
                        }`}
                      >
                        {heuristicLabel(row.heuristic)}
                      </span>
                    </td>
                    <td className="max-w-[400px] px-4 py-2.5 text-neutral-700">
                      {truncateText(row.question, 80)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${
                          STATUS_COLORS[row.status] ?? FALLBACK_PILL
                        }`}
                      >
                        {statusLabel(row.status)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {listing && listing.total > 0 && (
          <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-2.5">
            <span className="text-xs text-neutral-500">
              {t("issues.pageOf", { page: listing.page, total: totalPages })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                ghost
                size="xs"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                {t("issues.prev")}
              </Button>
              <Button
                ghost
                size="xs"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                {t("issues.next")}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {(selected || drawerLoading) && (
        <IssueDrawer
          issue={selected}
          loading={drawerLoading}
          showReport={!fixedReportKey}
          heuristicLabel={heuristicLabel}
          statusLabel={statusLabel}
          onClose={() => setSelected(null)}
          onActionDone={(fresh) => void handleActionDone(fresh)}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// Drawer — detail + triage actions
// ───────────────────────────────────────────────────────────

type ActionMode = "promote" | "resolve" | "dismiss" | null;

interface IssueDrawerProps {
  issue: IssueDetail | null;
  loading: boolean;
  showReport: boolean;
  heuristicLabel: (h: string) => string;
  statusLabel: (s: string) => string;
  onClose: () => void;
  onActionDone: (fresh: IssueDetail) => void;
}

function IssueDrawer({
  issue,
  loading,
  showReport,
  heuristicLabel,
  statusLabel,
  onClose,
  onActionDone,
}: IssueDrawerProps): JSX.Element {
  const { t } = useT();
  // Mounted only while open. Focus contract (QA F20, 2026-10-26): Escape
  // closes it (it used to leave the backdrop catching every click), Tab
  // stays inside, the focus goes back to the row that opened it.
  const panelRef = useRef<HTMLDivElement>(null);
  useDialogFocus(true, panelRef, onClose);

  const [actionMode, setActionMode] = useState<ActionMode>(null);
  const [promoteQuestion, setPromoteQuestion] = useState("");
  const [promoteSql, setPromoteSql] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Re-seed the promote form whenever a different issue lands.
  useEffect(() => {
    setActionMode(null);
    setPromoteQuestion(issue?.question ?? "");
    setPromoteSql("");
    setNotes("");
    setActionError(null);
  }, [issue?.id, issue?.question]);

  const runAction = async (
    fn: () => Promise<{ issue: IssueDetail }>,
  ): Promise<void> => {
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fn();
      setActionMode(null);
      onActionDone(res.issue);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePromote = (): void => {
    if (!issue) return;
    if (!promoteQuestion.trim() || !promoteSql.trim()) {
      setActionError(t("issues.promote.required"));
      return;
    }
    void runAction(() =>
      adminIssuesApi.resolve(issue.id, {
        action: "promoted_to_example",
        notes: notes.trim() || undefined,
        training_example_question: promoteQuestion.trim(),
        training_example_sql: promoteSql.trim(),
      }),
    );
  };

  const handleResolve = (action: Exclude<IssueResolveAction, "promoted_to_example">): void => {
    if (!issue) return;
    void runAction(() =>
      adminIssuesApi.resolve(issue.id, {
        action,
        notes: notes.trim() || undefined,
      }),
    );
  };

  const handleDismiss = (reason: IssueDismissReason): void => {
    if (!issue) return;
    void runAction(() =>
      adminIssuesApi.dismiss(issue.id, {
        reason,
        notes: notes.trim() || undefined,
      }),
    );
  };

  const handleReopen = (): void => {
    if (!issue) return;
    void runAction(() => adminIssuesApi.reopen(issue.id));
  };

  const isOpen = issue?.status === "new" || issue?.status === "in_review";

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="issue-drawer-title"
        className="h-full w-[640px] max-w-full overflow-y-auto bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: the kit's v3 SidePanel head (white, a line under it,
            the title in Nunito Sans), not the old dark gradient. */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-neutral-200 bg-white px-5 py-4">
          <div className="min-w-0 flex-1">
            <h3 id="issue-drawer-title" className="font-display text-[15px] font-extrabold leading-6 text-neutral-800">
              {issue
                ? t("issues.drawer.title", { id: issue.id })
                : t("common.loading")}
            </h3>
            {issue && (
              <p className="mt-0.5 text-xs text-neutral-600">
                {showReport ? `${issue.report_key} · ` : ""}
                {formatDateTime(issue.created_at)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mr-1.5 shrink-0 rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800"
            aria-label={t("common.close")}
          >
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={20} ariaLabel={t("common.loading")} />
          </div>
        )}

        {!loading && issue && (
          <div className="space-y-5 p-5">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-block rounded-full border px-2.5 py-1 text-xs font-medium ${
                  STATUS_COLORS[issue.status] ?? FALLBACK_PILL
                }`}
              >
                {statusLabel(issue.status)}
              </span>
              <span
                className={`inline-block rounded-full border px-2.5 py-1 text-xs font-medium ${
                  HEURISTIC_COLORS[issue.heuristic] ?? FALLBACK_PILL
                }`}
              >
                {heuristicLabel(issue.heuristic)}
              </span>
              {issue.resolution_action && (
                <Badge variant="success" size="sm" pill>
                  {issue.resolution_action}
                </Badge>
              )}
            </div>

            <DrawerSection title={t("issues.drawer.question")}>
              <p className="whitespace-pre-wrap text-sm text-neutral-900">
                {issue.question}
              </p>
            </DrawerSection>

            {issue.response && (
              <DrawerSection title={t("issues.drawer.response")}>
                <p className="whitespace-pre-wrap rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700">
                  {issue.response}
                </p>
              </DrawerSection>
            )}

            {issue.heuristic_detail && (
              <DrawerSection title={t("issues.drawer.heuristicDetail")}>
                <p className="rounded border border-neutral-200 bg-neutral-50 p-2 font-mono text-xs text-neutral-600">
                  {issue.heuristic_detail}
                </p>
              </DrawerSection>
            )}

            {issue.tool_calls.length > 0 && (
              <DrawerSection
                title={t("issues.drawer.toolCalls", {
                  count: issue.tool_calls.length,
                })}
              >
                <pre className="max-h-64 overflow-x-auto overflow-y-auto rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-700">
                  {JSON.stringify(issue.tool_calls, null, 2)}
                </pre>
              </DrawerSection>
            )}

            {issue.status !== "new" && issue.resolution_notes && (
              <DrawerSection title={t("issues.drawer.resolutionNotes")}>
                <p className="rounded-lg border border-success/40 bg-success/10 p-3 text-sm text-neutral-700">
                  {issue.resolution_notes}
                </p>
              </DrawerSection>
            )}

            {actionError && (
              <div className="flex items-start gap-2 rounded-lg border border-error/40 bg-error/5 px-3 py-2 text-sm text-error-dark">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span className="flex-1">{actionError}</span>
              </div>
            )}

            {/* Actions */}
            {isOpen ? (
              <div className="space-y-3 border-t border-neutral-200 pt-5">
                {actionMode === null && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setActionMode("promote")}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-success px-3 py-2 text-sm font-medium text-white hover:bg-success/80"
                    >
                      <CheckCircle2 size={14} />
                      {t("issues.action.promote")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActionMode("resolve");
                        setNotes("");
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
                    >
                      {t("issues.action.resolve")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActionMode("dismiss");
                        setNotes("");
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
                    >
                      <XCircle size={14} />
                      {t("issues.action.dismiss")}
                    </button>
                  </div>
                )}

                {actionMode === "promote" && (
                  <div className="space-y-3 rounded-lg border border-success/40 bg-success/10 p-4">
                    <h4 className="text-sm font-semibold text-success-dark">
                      {t("issues.action.promote")}
                    </h4>
                    <label className="block">
                      <span className="mb-1 block text-xs font-semibold text-neutral-500">
                        {t("issues.promote.question")}
                      </span>
                      <input
                        type="text"
                        value={promoteQuestion}
                        onChange={(e) => setPromoteQuestion(e.target.value)}
                        className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-semibold text-neutral-500">
                        {t("issues.promote.sql")}
                      </span>
                      <textarea
                        value={promoteSql}
                        onChange={(e) => setPromoteSql(e.target.value)}
                        placeholder="SELECT …"
                        rows={6}
                        className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 font-mono text-xs"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-semibold text-neutral-500">
                        {t("issues.notes")}
                      </span>
                      <input
                        type="text"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm"
                      />
                    </label>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        ghost
                        size="sm"
                        onClick={() => setActionMode(null)}
                        disabled={submitting}
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handlePromote}
                        disabled={submitting}
                        loading={submitting}
                      >
                        {t("issues.promote.save")}
                      </Button>
                    </div>
                  </div>
                )}

                {actionMode === "resolve" && (
                  <ReasonPanel
                    title={t("issues.action.resolve")}
                    notes={notes}
                    onNotes={setNotes}
                    submitting={submitting}
                    onCancel={() => setActionMode(null)}
                    reasons={[
                      {
                        key: "known_limitation",
                        label: t("issues.resolve.known_limitation"),
                        run: () => handleResolve("known_limitation"),
                      },
                      {
                        key: "added_doc",
                        label: t("issues.resolve.added_doc"),
                        run: () => handleResolve("added_doc"),
                      },
                      {
                        key: "wont_fix",
                        label: t("issues.resolve.wont_fix"),
                        run: () => handleResolve("wont_fix"),
                      },
                    ]}
                  />
                )}

                {actionMode === "dismiss" && (
                  <ReasonPanel
                    title={t("issues.action.dismiss")}
                    notes={notes}
                    onNotes={setNotes}
                    submitting={submitting}
                    onCancel={() => setActionMode(null)}
                    reasons={[
                      {
                        key: "wont_fix",
                        label: t("issues.dismiss.wont_fix"),
                        run: () => handleDismiss("wont_fix"),
                      },
                      {
                        key: "duplicate",
                        label: t("issues.dismiss.duplicate"),
                        run: () => handleDismiss("duplicate"),
                      },
                      {
                        key: "known_limitation",
                        label: t("issues.dismiss.known_limitation"),
                        run: () => handleDismiss("known_limitation"),
                      },
                    ]}
                  />
                )}
              </div>
            ) : (
              <div className="border-t border-neutral-200 pt-5">
                <button
                  type="button"
                  onClick={handleReopen}
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                >
                  <RotateCcw size={14} />
                  {t("issues.action.reopen")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DrawerSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold text-neutral-500">
        {title}
      </h4>
      {children}
    </div>
  );
}

function ReasonPanel({
  title,
  notes,
  onNotes,
  submitting,
  onCancel,
  reasons,
}: {
  title: string;
  notes: string;
  onNotes: (v: string) => void;
  submitting: boolean;
  onCancel: () => void;
  reasons: Array<{ key: string; label: string; run: () => void }>;
}): JSX.Element {
  const { t } = useT();
  return (
    <div className="space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
      <h4 className="text-sm font-semibold text-neutral-900">{title}</h4>
      <input
        type="text"
        value={notes}
        onChange={(e) => onNotes(e.target.value)}
        placeholder={t("issues.notes")}
        className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm"
      />
      <div className="flex flex-wrap items-center gap-2">
        {reasons.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={r.run}
            disabled={submitting}
            className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-50"
          >
            {r.label}
          </button>
        ))}
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-lg px-3 py-1.5 text-xs text-neutral-600 hover:bg-white"
        >
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}
